import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotificationContext } from '../../providers/NotificationProvider';
import { DashboardSection } from '../../components/DashboardSection/DashboardSection';
import { getSessionVar } from '../../lib/helpers';
import { ROUTE_CONFIG } from '../../constants/routes';
import { UserRoles } from '../../constants/index';
import {
    parseProjectDetailsResponse,
    projectDetailsCardValues,
    parseSourcesResponse,
    sourcesCardValues,
    parseMigrationStatusResponse,
    migrationStatusCardValues,
    parseMigrationAppliancesResponse,
    migrationAppliancesCardValues,
} from '../../components/Card/CardParsers';
import { CARD_TYPES } from '../../components/Card/cardTypes';
import { SummaryCardRenderer, buildCards } from '../../components/Card/SummaryCardRenderer';
import './MigrationDashboard.scss';

const EMPTY_ORG = {};

export const MigrationDashboard = ({ migrationData, getUserRole }) => {
    const navigate = useNavigate();
    const { notifyError } = useNotificationContext();
    const currentOrg = getSessionVar('currentOrg') || EMPTY_ORG;
    const isGlobalAdmin = getUserRole() === UserRoles.GLOBAL_ADMIN;
    const isAdmin = getUserRole() === UserRoles.ADMIN || getUserRole() === UserRoles.ORG_ADMIN;

    const handleCardClick = useCallback(
        (link) => {
            if (isGlobalAdmin) return;
            if (link === ROUTE_CONFIG.projects.path && isAdmin) {
                navigate(link);
            } else if (link === ROUTE_CONFIG.projects.path && !isAdmin) {
                notifyError({ description: 'You do not have permission to access this page.' });
            } else {
                navigate(link);
            }
        },
        [isGlobalAdmin, isAdmin, navigate, notifyError],
    );

    const getCardConfigs = () => [
        {
            id: 'projectDetails',
            cardType: CARD_TYPES.PROJECT_DETAILS,
            parseResponse: parseProjectDetailsResponse,
            values: projectDetailsCardValues,
        },
        { id: 'sources', cardType: CARD_TYPES.STAT, parseResponse: parseSourcesResponse, values: sourcesCardValues },
        {
            id: 'migrationStatus',
            cardType: CARD_TYPES.MIGRATION_STATUS,
            parseResponse: parseMigrationStatusResponse,
            values: migrationStatusCardValues,
        },
        {
            id: 'migrationAppliances',
            cardType: CARD_TYPES.MIGRATION_APPLIANCES,
            parseResponse: parseMigrationAppliancesResponse,
            values: migrationAppliancesCardValues,
        },
    ];

    const cards = useMemo(
        () =>
            buildCards(getCardConfigs(), { currentOrg, newMigrationData: migrationData, isAdmin }, { handleCardClick }),
        [currentOrg, migrationData, isAdmin, handleCardClick],
    );

    return (
        <DashboardSection title="Migration" className="migration-dashboard">
            <div className="migration-dashboard-content">
                <SummaryCardRenderer cards={cards} />
            </div>
        </DashboardSection>
    );
};
